import { useEffect, useState } from "react";
import { Logo } from "../components/Logo";
import { useAuth, ApiError } from "../auth/AuthContext";

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;

// Messages for the failure codes the server-side OAuth callback redirects with
// (/login?error=…). Keeps the not-an-operator copy consistent with the dev path.
const CALLBACK_ERRORS: Record<string, string> = {
  not_an_operator:
    "That account isn't an operator. Memex Backstage is restricted to the operator domain allowlist.",
  email_unverified:
    "That Google account's email isn't verified, so it can't be used to sign in.",
  auth_failed: "Sign-in failed. Please try again.",
};

// SSO-only login (dec-2). With a Google client id configured the operator signs
// in via the server-side OAuth authorization-code redirect (a full navigation to
// /api/auth/google/start → Google → /api/auth/google/callback, which hands a
// session JWT back via the URL fragment). This uses the OAuth client's Authorised
// *redirect URI*, not the GIS JavaScript-origin allowlist. With no client id
// (local dev / e2e) the server runs in dev-auth mode and we offer a bare email
// field instead — there is never a password.
export function LoginPage() {
  const { loginWithIdToken } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [devEmail, setDevEmail] = useState("");

  // Surface an error handed back by the OAuth callback, then strip it from the
  // URL so a refresh doesn't keep re-showing it.
  useEffect(() => {
    const code = new URLSearchParams(window.location.search).get("error");
    if (code) {
      setError(CALLBACK_ERRORS[code] ?? "Sign-in failed. Please try again.");
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, []);

  async function submit(idToken: string) {
    setError(null);
    try {
      await loginWithIdToken(idToken);
    } catch (e) {
      if (e instanceof ApiError && e.status === 403) {
        setError(
          "That account isn't an operator. Memex Backstage is restricted to the operator domain allowlist.",
        );
      } else {
        setError("Sign-in failed. Please try again.");
      }
    }
  }

  return (
    <div className="h-screen flex items-center justify-center bg-page">
      <div className="w-full max-w-sm rounded-2xl border border-edge bg-surface p-8 space-y-6">
        <div className="flex justify-center">
          <Logo className="h-8" />
        </div>
        <div className="text-center space-y-1">
          <h1 className="text-lg text-heading">Operator console</h1>
          <p className="text-sm text-muted">
            Sign in with your operator Google account to continue.
          </p>
        </div>

        {GOOGLE_CLIENT_ID ? (
          <a
            href="/api/auth/google/start"
            data-testid="google-signin"
            className="flex items-center justify-center gap-3 w-full rounded-lg border border-edge bg-white px-3 py-2.5 text-sm font-medium text-[#1f1f1f] transition-colors hover:bg-gray-50"
          >
            <GoogleMark />
            Sign in with Google
          </a>
        ) : (
          <form
            data-testid="dev-login"
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (devEmail) void submit(devEmail);
            }}
          >
            <p className="text-xs text-muted">
              Dev-auth mode (no Google client configured). Enter an operator email.
            </p>
            <input
              type="email"
              required
              placeholder="you@mindset.ai"
              value={devEmail}
              onChange={(e) => setDevEmail(e.target.value)}
              className="w-full rounded-lg border border-edge bg-input px-3 py-2 text-sm text-primary outline-none focus:border-accent"
            />
            <button
              type="submit"
              className="w-full rounded-lg bg-accent px-3 py-2 text-sm font-medium text-on-accent transition-colors hover:bg-accent-hover"
            >
              Continue
            </button>
          </form>
        )}

        {error && (
          <p
            data-testid="login-error"
            className="rounded-lg border border-status-danger-border bg-status-danger-bg px-3 py-2 text-sm text-status-danger-text"
          >
            {error}
          </p>
        )}
      </div>
    </div>
  );
}

/** The Google "G" mark for the sign-in button. */
function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M17.64 9.2045c0-.6381-.0573-1.2518-.1636-1.8409H9v3.4814h4.8436c-.2086 1.125-.8427 2.0782-1.7959 2.7164v2.2581h2.9087c1.7018-1.5668 2.6836-3.874 2.6836-6.615z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.4673-.806 5.9564-2.1805l-2.9087-2.2581c-.806.54-1.8368.8591-3.0477.8591-2.344 0-4.3282-1.5831-5.0359-3.7104H.9573v2.3318C2.4382 15.9832 5.4818 18 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.9641 10.71c-.18-.54-.2823-1.1168-.2823-1.71s.1023-1.17.2823-1.71V4.9582H.9573C.3477 6.1732 0 7.5477 0 9s.3477 2.8268.9573 4.0418L3.9641 10.71z"
      />
      <path
        fill="#EA4335"
        d="M9 3.5795c1.3214 0 2.5077.4541 3.4405 1.346l2.5813-2.5814C13.4632.8918 11.426 0 9 0 5.4818 0 2.4382 2.0168.9573 4.9582L3.9641 7.29C4.6718 5.1627 6.656 3.5795 9 3.5795z"
      />
    </svg>
  );
}
